<script setup lang="ts">
import type { KjdsDetailPageProps } from '@/types/appTypes'

const props = withDefaults(defineProps<KjdsDetailPageProps>(), {})

const app = useNuxtApp()
const route = useRoute()
const storeArticleDetail = useStoreArticleDetail()
const storeIndex = useStoreIndex()
const storeUser = useStoreUser()

const baseTitle = '展覧会'
let headTitle = ''
let headDescription = ''
const refData = reactive({
  eventNowCount: 2,
  eventFutureCount: 1,
  rating: 3,
  aTag: null, // タグリスト
  jyunkaiItems: [] as Article[],
})

const setHead = () => {
  const articleTitle = props.kjdsDetailData.article.title
  headTitle = `${articleTitle} | 見どころ・チケット・口コミ`
  headDescription = `アイエム［インターネットミュージアム］による ${articleTitle} の情報ページ。${articleTitle} の見どころ、会期、料金、休館日などを紹介しています。読者によるクチコミも。${articleTitle}のことならアイエムで！`
  if (props.kjdsDetailData.article.description?.length) {
    headDescription = props.kjdsDetailData.article.description
  }
  // ぱんくず作成
  if (route.query.fromname === 'foryou') {
    storeIndex.setBreadCrumbs([
      {
        text: 'For You',
        disabled: false,
        to: {
          name: 'mypage-foryou-tab',
          params: { tab: 'event' },
        },
      },
      {
        text: articleTitle,
        disabled: true,
      },
    ])
  } else {
    storeIndex.setBreadCrumbs([
      {
        text: baseTitle,
        disabled: false,
        to: { path: '/event' },
      },
      {
        text: articleTitle,
        disabled: true,
      },
    ])
  }

  const headInput = app.$makeMeta({
    title: headTitle,
    description: headDescription,
    ogImage: props.kjdsDetailData.eyeCatch?.url ? props.kjdsDetailData.eyeCatch.url : undefined,
  })
  useHead(headInput)
}

const refPoint = ref(props.kjdsDetailData.article.point)

const onClickMemo = () => {
  const toPath = `/mypage/memo/0/edit?event=${props.kjdsDetailData.article.article_slug}`
  if (storeUser.state.isLogin) {
    navigateTo(toPath)
  } else {
    storeUser.setLoginRedirectPath(toPath)
    navigateTo('/login')
  }
}

// イベントから表示カラム作成
if (!refData.aTag) {
  refData.aTag = app.$getArticleTags(props.kjdsDetailData.article)
} // タグリスト

onMounted(() => {
  // 関連巡回展からイベント取得
  const jyunkaiRelArticle = app.$getArticleRelationsSingle(props.kjdsDetailData.article, 'jyunkai', null)
  if (jyunkaiRelArticle) {
    storeArticleDetail.fetchDetail({
      fetchParams: {
        articleTypeSlug: 'jyunkai',
        articleSlugOrId: jyunkaiRelArticle.article_slug,
      },
      isNoThrowError: true,
    })
      .then((res) => {
        refData.jyunkaiItems.push(
          res.data.article,
        )
      })
  }
})

setHead()
</script>

<template>
  <div
    v-if="kjdsDetailData"
    class="p-articleDetailPage"
  >
    <div class="l-mainOverview">
      <EventBody
        v-bind:item="kjdsDetailData.article"
        v-bind:objects="kjdsDetailData.objects"
        v-bind:title="headTitle"
      />
    </div>

    <div class="c-link">
      <div class="c-link_first">
        <Clip
          class="c-link_first_clip"
          v-bind:mode="true"
          v-bind:dspcnt="true"
          v-bind:article-type-slug="kjdsDetailData.article.article_type_slug"
          v-bind:article-slug="kjdsDetailData.article.article_slug"
          v-bind:count="kjdsDetailData.article.user_favorite_count"
        />
        <div>
          <button
            class="c-link_first_comment"
            v-on:click="onClickMemo"
          >
            <img
              class="c-link_first_comment_img"
              src="@/assets/images/icon-edit-msg2.svg"
            >
            感想を書く
          </button>
        </div>
      </div>
      <ShareButtons
        v-bind:title="headTitle"
      />
    </div>
    <div
      v-if="refPoint > 0"
      class="l-evaluation"
    >
      <span class="l-evaluation_text">評価</span>
      <v-rating
        v-model="refPoint"
        empty-icon="mdi-star-outline"
        full-icon="mdi-star"
        color="primary"
        length="5"
        size="20"
        readonly
      />
    </div>
    <div
      class="l-shareImpression"
    >
      <NuxtLink
        class="l-shareImpression_button"
        v-bind:to="`/event/${props.kjdsDetailData.article.article_slug}/memo`"
      >
        <img
          class="l-shareImpression_button_img"
          src="@/assets/images/icon-edit-msg.svg"
        >
        みんなの感想
      </NuxtLink>
    </div>

    <ArticleTagList
      v-if="refData.aTag.length > 0"
      v-bind:article-name="kjdsDetailData.article.article_type_slug"
      v-bind:items="refData.aTag"
      class="pc-only"
    />

    <template v-if="app.$isEvent(props.kjdsDetailData.article)">
      <RelationBox
        article="event"
        v-bind:header="'展覧会'"
        v-bind:relations="kjdsDetailData.article.relations"
      />
      <RelationBox
        article="special"
        v-bind:header="'関連する特集'"
        v-bind:relations="kjdsDetailData.article.relations"
        event
      />
      <RelationBox
        article="present"
        v-bind:header="kjdsDetailData.article.title + ' のプレゼント'"
        v-bind:relations="kjdsDetailData.article.relations"
      />
    </template>
    <template v-else>
      <RelationBox
        article="present"
        v-bind:header="kjdsDetailData.article.title + ' のプレゼント'"
        v-bind:relations="kjdsDetailData.article.relations"
      />
      <RelationBox
        article="news"
        v-bind:header="kjdsDetailData.article.title + ' のニュース'"
        v-bind:relations="kjdsDetailData.article.relations"
      />
      <RelationBox
        article="report"
        v-bind:header="kjdsDetailData.article.title + ' のレポート'"
        v-bind:relations="kjdsDetailData.article.relations"
        v-bind:is-past="true"
        v-bind:objects="kjdsDetailData.objects"
      />
      <RelationBox
        article="event"
        v-bind:header="kjdsDetailData.article.title + ' のイベント'"
        v-bind:relations="kjdsDetailData.article.relations"
        v-bind:is-past="true"
        event
      />
      <RelationBox
        v-if="refData.jyunkaiItems.length > 0"
        article="jyunkai"
        v-bind:header="'関連する巡回展'"
        v-bind:articles="refData.jyunkaiItems"
      />
      <RelationBox
        article="special"
        v-bind:header="'関連する特集'"
        v-bind:relations="kjdsDetailData.article.relations"
      />
    </template>

    <ArticleTagList
      v-if="refData.aTag.length > 0"
      v-bind:article-name="kjdsDetailData.article.article_type_slug"
      v-bind:items="refData.aTag"
      class="sp-only"
    />
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive";

.l-eventRelated {
  margin-top: 105px;
}

.l-eventFeatured {
  margin-top: 73px;
}

.l-eventGift {
  margin-top: 63px;
}

.l-eventTraveling {
  margin-top: 102px;
}

.l-shareImpression {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 45px;
  margin-bottom: 45px;

  &_button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 400px;
    height: 47px !important;
    margin-left: -3px;
    border: none;
    border-radius: 6px;
    background-color: var(--color_primary);
    color: var(--g_color_text_white);
    font-family: var(--g_font_family_SawarabiGothic);
    font-size: 16px;
    white-space: nowrap;

    @include responsive.mq_down(sp) {
      width: 100%;
      max-width: 400px;
      margin: 0;
    }

    &_img {
      width: 24px;
      max-width: 24px;
      height: 24px;
      max-height: 24px;
      margin-right: 13px;
    }

    &:hover {
      opacity: 0.7;
    }
  }

  &_description {
    margin-top: 24px;
    font-family: var(--g_font_family_NotoSansCJKjp);
    font-size: 14px;
    font-weight: bold;
    line-height: 1.571;
    text-align: center;
  }
}

.l-recommend {
  margin-top: 90px;
  margin-bottom: 98px;

  &_title {
    padding-bottom: 15px;
    color: black;
    font-family: var(--g_font_family_SawarabiGothic);
    font-size: 20px;
    font-weight: bold;
    line-height: 1.6;
    text-align: left;
  }

  &_content {
    margin-top: 15px;
  }
}

.l-advertise {
  width: 336px;
  height: 280px;
  background-color: yellowgreen;
}

.l-population {
  margin-top: 24px;
}

.l-sideNew {
  margin-top: 24px;
}

.l-sideItems {
  margin-top: 24px;
}

.l-news {
  margin-top: 24px;
}

.l-bannerGroup {
  margin-top: 24px;
}

.c-link {
  display: flex;
  justify-content: center;
  margin-top: 35px;

  @include responsive.mq_down(sp) {
    flex-direction: column;
    align-items: center;
  }

  & > div {
    @include responsive.mq_down(sp) {
      margin: 5px 0;
    }

    &:last-of-type {
      @include responsive.mq_down(sp) {
        margin-top: 15px;
      }
    }
  }

  &_first {
    display: flex;
    justify-content: center;

    @include responsive.mq_down(small) {
      flex-direction: column;
      align-items: center;
    }

    &_comment {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 152px;
      height: 40px;
      margin: 0 20px;
      border: 1px solid var(--color_border_control);
      border-radius: 50px;
      font-size: 14px;
      font-weight: bold;
      letter-spacing: normal;

      @include responsive.mq_down(sp) {
        margin: 0 0 0 20px;
      }

      @include responsive.mq_down(small) {
        margin: 20px 0 0;
      }

      &:hover {
        opacity: 0.7;
      }

      &_img {
        width: 21px;
        height: 21px;
        margin-right: 10px;
      }
    }
  }

  &_button {
    display: flex;
    margin: 0 20px;

    &_border {
      min-width: 60px !important;
      min-height: 40px;

      &:hover {
        opacity: 0.7;
      }
    }

    &_middle {
      border-right: 0;
      border-left: 0;
    }
  }
}

.l-partent {
  &_header {
    margin-bottom: 40px;
    padding-bottom: 19px;
    border-bottom: 1px solid var(--g_color_border_gray);

    @include responsive.mq_down(sp) {
      margin-bottom: 30px;
      padding-bottom: 9px;
    }

    &_text {
      margin-top: 15px;
      color: var(--color_text_base);
      font-family: var(--g_font_family_KozukaGothicPr6N);
      font-size: 20px;
      font-weight: 500;
      line-height: 0.923;

      @include responsive.mq_down(sp) {
        font-size: 18px;
        line-height: inherit;
      }

      &::before {
        content: "";
        display: inline-block;
        width: 26px;
        height: 26px;
        margin-top: -3px;
        margin-right: 5px;
        background-image: url("@/assets/images/icon-title.png");
        vertical-align: middle;

        @include responsive.mq_down(sp) {
          width: 20px;
          height: 20px;
          background-size: cover;
        }
      }
    }
  }
}

.l-evaluation {
  justify-content: center;
  margin: 60px 0 43px;

  .v-rating {
    background-color: var(--g_color_image_background_f1f4f6);
  }
}
</style>
